import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import {
  Container,
  Paper,
  Typography,
  Box,
  Avatar,
  Chip,
  CircularProgress,
  Alert,
  Button,
  Stack,
} from '@mui/material';
import {
  Email as EmailIcon,
  Phone as PhoneIcon,
  Business as BusinessIcon,
  Work as WorkIcon,
  CalendarToday as CalendarIcon,
  Download as DownloadIcon,
  QrCode as QrCodeIcon,
  Badge as BadgeIcon,
} from '@mui/icons-material';
import { employeeAPI } from '../services/api';
import { getProfileUrl, downloadQrCode, getImageUrl } from '../utils/urlUtils';
import '../styles/AppShell.css';

const EmployeeProfile = () => {
  const { employee_code } = useParams();
  const [employee, setEmployee] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const fetchEmployee = useCallback(async () => {
    try {
      setLoading(true);
      const response = await employeeAPI.getByCode(employee_code);
      setEmployee(response.data.employee);
    } catch (err) {
      setError('Employee not found or inactive');
    } finally {
      setLoading(false);
    }
  }, [employee_code]);

  useEffect(() => {
    fetchEmployee();
  }, [fetchEmployee]);

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const raw = String(dateString).trim();
    const iso = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (iso) return `${iso[3]}-${iso[2]}-${iso[1]}`;
    const d = new Date(raw);
    if (Number.isNaN(d.getTime())) return 'N/A';
    return d.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  };

  const handleDownloadQR = async () => {
    if (!employee?.qr_code_url) return;
    await downloadQrCode(employee.qr_code_url, employee.employee_code);
  };

  if (loading) {
    return (
      <div className="app-shell">
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
          <CircularProgress />
        </Box>
      </div>
    );
  }

  if (error || !employee) {
    return (
      <div className="app-shell">
        <Container maxWidth="sm" sx={{ pt: 8 }}>
          <Alert severity="error" sx={{ borderRadius: 2 }}>
            {error || 'Employee profile not found'}
          </Alert>
        </Container>
      </div>
    );
  }

  const photoSrc = employee.photo_url ? getImageUrl(employee.photo_url) : undefined;
  const qrSrc = employee.qr_code_url ? getImageUrl(employee.qr_code_url) : null;

  const details = [
    { icon: <BadgeIcon fontSize="small" />, label: 'Employee ID', value: employee.employee_code },
    { icon: <EmailIcon fontSize="small" />, label: 'Email', value: employee.email || 'N/A' },
    { icon: <PhoneIcon fontSize="small" />, label: 'Phone', value: employee.phone || 'N/A' },
    { icon: <BusinessIcon fontSize="small" />, label: 'Department', value: employee.department || 'N/A' },
    {
      icon: <WorkIcon fontSize="small" />,
      label: 'Designation',
      value: employee.designation || employee.position || 'N/A',
    },
    { icon: <CalendarIcon fontSize="small" />, label: 'Date of joining', value: formatDate(employee.hire_date) },
    { icon: <CalendarIcon fontSize="small" />, label: 'Date of birth', value: formatDate(employee.date_of_birth) },
    { icon: <CalendarIcon fontSize="small" />, label: 'Date of issue', value: formatDate(employee.date_of_issue) },
  ];

  return (
    <div className="app-shell">
      <Container maxWidth="md" sx={{ py: { xs: 3, md: 5 } }}>
        <Box className="profile-hero" sx={{ mb: 3, position: 'relative', zIndex: 1 }}>
          <Box display="flex" alignItems="center" gap={1.5} mb={3} position="relative" zIndex={1}>
            <img
              src="/images/logo.png"
              alt="Star Security"
              style={{ width: 40, height: 40, borderRadius: 10, background: '#fff', padding: 3 }}
            />
            <Box>
              <Typography sx={{ fontWeight: 800, letterSpacing: '-0.01em' }}>
                Star Security Services
              </Typography>
              <Typography variant="caption" sx={{ opacity: 0.85 }}>
                Verified employee profile
              </Typography>
            </Box>
          </Box>

          <Box
            display="flex"
            flexDirection={{ xs: 'column', sm: 'row' }}
            alignItems={{ xs: 'center', sm: 'flex-start' }}
            gap={2.5}
            position="relative"
            zIndex={1}
            textAlign={{ xs: 'center', sm: 'left' }}
          >
            <Avatar
              src={photoSrc}
              alt={`${employee.first_name} ${employee.last_name}`}
              sx={{
                width: 112,
                height: 112,
                border: '4px solid rgba(255,255,255,0.9)',
                fontSize: 36,
                fontWeight: 700,
                boxShadow: '0 8px 24px rgba(0,0,0,0.2)',
              }}
            >
              {employee.first_name?.charAt(0)}
              {employee.last_name?.charAt(0)}
            </Avatar>
            <Box flex={1}>
              <Typography
                variant="h4"
                sx={{ fontWeight: 800, letterSpacing: '-0.02em', mb: 0.5 }}
              >
                {employee.first_name} {employee.last_name}
              </Typography>
              <Typography variant="body1" sx={{ opacity: 0.9, mb: 1.5 }}>
                {employee.designation || employee.position || 'Employee'}
              </Typography>
              <Chip
                label={employee.is_active ? 'Active' : 'Inactive'}
                size="small"
                sx={{
                  bgcolor: employee.is_active ? 'rgba(124,179,66,0.95)' : 'rgba(198,40,40,0.9)',
                  color: employee.is_active ? '#0B1D4A' : '#fff',
                  fontWeight: 750,
                }}
              />
            </Box>
          </Box>
        </Box>

        <Paper elevation={0} className="section-panel" sx={{ p: { xs: 2.5, md: 3.5 }, mb: 3 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 750, mb: 1 }}>
            Profile details
          </Typography>
          {details.map((row) => (
            <div className="profile-detail-row" key={row.label}>
              <div className="profile-detail-icon">{row.icon}</div>
              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 650, letterSpacing: '0.04em' }}>
                  {row.label.toUpperCase()}
                </Typography>
                <Typography sx={{ fontWeight: 650 }}>{row.value}</Typography>
              </Box>
            </div>
          ))}
          {employee.address && (
            <div className="profile-detail-row">
              <div className="profile-detail-icon">
                <BusinessIcon fontSize="small" />
              </div>
              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 650, letterSpacing: '0.04em' }}>
                  ADDRESS
                </Typography>
                <Typography sx={{ fontWeight: 650 }}>{employee.address}</Typography>
              </Box>
            </div>
          )}
        </Paper>

        {qrSrc && (
          <Paper elevation={0} className="section-panel" sx={{ p: 3.5, textAlign: 'center', mb: 3 }}>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ fontWeight: 750, letterSpacing: '0.1em', display: 'block', mb: 2 }}
            >
              <QrCodeIcon sx={{ fontSize: 14, verticalAlign: 'middle', mr: 0.5 }} />
              SCAN TO VERIFY
            </Typography>
            <Box
              sx={{
                display: 'inline-block',
                p: 2,
                borderRadius: 3,
                border: '1px solid',
                borderColor: 'divider',
                bgcolor: '#fff',
              }}
            >
              <img src={qrSrc} alt="QR Code" style={{ width: 180, height: 180, display: 'block' }} />
            </Box>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} justifyContent="center" sx={{ mt: 2.5 }}>
              <Button variant="contained" startIcon={<DownloadIcon />} onClick={handleDownloadQR}>
                Download QR
              </Button>
              <Button
                variant="outlined"
                onClick={() => window.open(`/card/${employee.employee_code}`, '_blank')}
              >
                Open ID card
              </Button>
            </Stack>
            <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
              {getProfileUrl(employee.employee_code)}
            </Typography>
          </Paper>
        )}

        <Box textAlign="center" pt={1}>
          <Typography variant="body2" color="text.secondary">
            © {new Date().getFullYear()} Star Security Services
          </Typography>
          <Typography variant="caption" color="text.secondary" display="block" mt={0.5}>
            Powered by EnvisionTech Solutions
          </Typography>
        </Box>
      </Container>
    </div>
  );
};

export default EmployeeProfile;
